"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";

import "../../marken/marken.css";

import { TopSectionMarken } from "../../marken/sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";

import höchstenPreis from "@/assets/auto-verkaufen/Frau-verkauft-ihr-auto.webp";
import BannerImage1 from "@/assets/auto-verkaufen/AutoeinfachlosBannerBig1.avif";
import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";

import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";
import { fragenWertverlustAutoGuide } from "./data";

// Ratgeber SERVERSEITIG rendern (dynamic OHNE die SSR-Abschaltung, Playbook 2.6):
// die sechs "Mehr zum Thema"-Kacheln verlinken u. a. /auto-verkaufen.
const Ratgeber = dynamic(() =>
  import("@/components/Ratgeber/Ratgeber").then((m) => m.default)
);

// Only needed when the user reaches step 12; avoid loading auth-related JS/CSS on initial paint.
const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function WertverlustAutoGuide() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionMarken
        title="Wertverlust Auto – verstehen, berechnen, minimieren"
        link="/guides/wertverlust-auto-guide"
        linkname="Wertverlust Auto"
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*{" "}
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung.
          </>
        }
        removePadding={true}
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Wertverlust beim Auto: So kannst du ihn berechnen"
              leftText={
                <>
                  <p>
                    Kaum ein Thema beschäftigt Autobesitzer so sehr wie der
                    Wertverlust ihres Fahrzeugs. Gleich nach der Erstzulassung
                    verliert ein Auto erheblich an Wert. Doch was genau
                    beeinflusst den Wert, wie kann man den Verlust reduzieren und
                    wann ist der optimale Verkaufszeitpunkt? Dieser ausführliche
                    Ratgeber klärt diese Fragen, erklärt die Mechanismen hinter
                    der Fahrzeugdegression und gibt praxisnahe Tipps, wie du den
                    Wiederverkaufswert maximieren kannst.
                  </p>
                </>
              }
              rightFirstTitle="Was bedeutet Wertverlust?"
              rightFirstText={
                <>
                  <p>
                    Unter Wertverlust versteht man die Differenz zwischen dem
                    Anschaffungspreis eines Fahrzeugs und seinem aktuellen
                    Marktwert. Der größte Teil des Wertverlustes findet in den
                    ersten Jahren statt: Bereits im ersten Jahr kann ein Neuwagen
                    bis zu 25 % seines Kaufpreises verlieren, nach drei Jahren
                    sind es oft mehr als 50 %. Danach verlangsamt sich der
                    Wertverlust, bis das Auto etwa nach 10 bis 15 Jahren in den
                    Bereich des Restwerts fällt. Dieser Restwert hängt stark von
                    der Nachfrage, der Marke, dem Modell und dem Zustand ab.
                  </p>

                  <h3>Faktoren, die den Wertverlust beeinflussen</h3>
                  <ul className="custom-unordered-list">
                    <li>
                      <strong>Marke und Modell:</strong> Fahrzeuge renommierter
                      Hersteller wie BMW, Audi oder Toyota verlieren langsamer an
                      Wert als seltene oder weniger beliebte Marken. Modelle mit
                      hoher Nachfrage am Gebrauchtwagenmarkt (z. B. VW Golf,
                      Skoda Octavia) sind wertstabiler.
                    </li>
                    <li>
                      <strong>Motorisierung:</strong> Autos mit sparsamem
                      Verbrauch und moderner Technik (z. B. Hybrid- oder
                      Elektroantrieb) sind gefragter als durstige Motoren. Bei
                      Verbrennern sind Diesel aktuell weniger gefragt wegen
                      Umweltzonen, was den Wert drückt.
                    </li>
                    <li>
                      <strong>Laufleistung:</strong> Kilometrierstand ist ein
                      wesentlicher Faktor. Als Faustregel gilt: 15 000 km pro
                      Jahr sind durchschnittlich. Jedes Mehr an Kilometern senkt
                      den Wert.
                    </li>
                    <li>
                      <strong>Ausstattung:</strong> Klimaanlage,
                      Navigationssystem, Assistenzsysteme oder Panoramadach
                      steigern den Wiederverkaufswert. Seltene Extras wie
                      Ledersitze oder spezielles Soundsystem können bei einer
                      kleinen Zielgruppe hohe Nachfrage erzeugen.
                    </li>
                    <li>
                      <strong>Zustand und Pflege:</strong> Ein
                      scheckheftgepflegtes Auto, regelmäßig gewartet und
                      unfallfrei, erzielt höhere Preise. Rost, Kratzer und
                      fehlende Servicehefte mindern den Wert.
                    </li>
                    <li>
                      <strong>Anzahl der Vorbesitzer:</strong> Je weniger Halter,
                      desto besser. Ein Auto aus erster Hand gilt als
                      vertrauenswürdig.
                    </li>
                    <li>
                      <strong>Wirtschaftliche Faktoren:</strong> Angebot und
                      Nachfrage schwanken regional und saisonal. Im Frühling ist
                      die Nachfrage höher als im Winter; Krisen beeinflussen
                      Kaufkraft und Preise.
                    </li>
                    <li>
                      <strong>Technologische Trends:</strong> Neue Abgasnormen
                      oder gesetzliche Änderungen (z. B. Fahrverbote für Diesel)
                      wirken sich auf den Marktwert aus.
                    </li>
                  </ul>
                </>
              }
              rightSecondTitle="Den Wertverlust berechnen"
              rightSecondText={
                <>
                  <p>
                    Um den aktuellen Wert deines Autos grob zu bestimmen, kannst
                    du folgende Methoden nutzen:
                  </p>
                  <ul className="custom-unordered-list secondary">
                    <li>
                      <strong>Abschreibungstabellen:</strong> Viele Automobilclubs
                      veröffentlichen Übersichten, wie viel Prozent des Neupreises
                      nach bestimmten Jahren typischerweise übrig bleiben.
                    </li>
                    <li>
                      <strong>Online-Bewertungstools:</strong> Anhand von
                      Fahrzeugdaten, Ausstattung und Laufleistung liefern sie
                      Schätzwerte.
                    </li>
                    <li>
                      <strong>Vergleich mit aktuellen Inseraten:</strong> Suche
                      nach gleichen oder ähnlichen Modellen in Onlinebörsen und
                      vergleiche die Preise.
                    </li>
                    <li>
                      <strong>Sachverständigengutachten:</strong> Besonders bei
                      teuren oder seltenen Autos lohnt sich ein Gutachten durch
                      einen Experten.
                    </li>
                  </ul>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={BannerImage1}
                revert
                h2
                isBig
                fontSize="36px"
                title="Tipps zur Wertminimierung"
                alt="Tipps zur Wertminimierung"
                text={
                  <>
                    <ul className="custom-unordered-list">
                      <li>
                        <strong>Pflege und Wartung:</strong> Regelmäßige Wartung
                        nach Herstellerangaben ist der wichtigste Faktor. Eine
                        vollständige Servicehistorie dokumentiert Vertrauen.
                      </li>
                      <li>
                        <strong>Kleinere Reparaturen sofort erledigen:</strong>{" "}
                        Beule oder Kratzer – lass sie zeitnah beseitigen.
                        Vernachlässigung führt zu Rost und erhöhten
                        Reparaturkosten.
                      </li>
                      <li>
                        <strong>Innenraum schützen:</strong> Sitzbezüge,
                        regelmäßiges Staubsaugen, keine rauchenden Mitfahrer – so
                        bleibt der Innenraum sauber und riecht angenehm.
                      </li>
                      <li>
                        <strong>Lackversiegelung und Unterbodenschutz:</strong>{" "}
                        Eine Schutzschicht bewahrt den Lack vor Umwelteinflüssen.
                        Unterbodenschutz verhindert Rostbildung.
                      </li>
                      <li>
                        <strong>Fahrweise anpassen:</strong> Sanftes Beschleunigen
                        und Bremsen schont Motor und Getriebe. Das spart nicht nur
                        Sprit, sondern reduziert Verschleiß.
                      </li>
                      <li>
                        <strong>Den richtigen Zeitpunkt wählen:</strong> Verkaufe
                        dein Auto nicht, wenn größere Reparaturen anstehen (z. B.
                        Zahnriemenwechsel). Frühzeitiger Verkauf kann sich lohnen.
                      </li>
                      <li>
                        <strong>Nachgefragte Extras wählen:</strong> Bei Neuwagenbestellung
                        lohnt es, Extras wie Sitzheizung oder Einparkhilfe zu wählen – sie
                        erhöhen den Wiederverkaufswert.
                      </li>
                      <li>
                        <strong>Markt beobachten:</strong> Trends erkennen und auf Elektro-
                        oder Hybridfahrzeuge umsteigen, bevor drohende Fahrverbote den Wert
                        von Verbrennern drücken.
                      </li>
                    </ul>

                    <h3>Beispielrechnung</h3>
                    <p>
                      Ein Kompaktwagen mit einem Neupreis von 25 000 Euro verliert
                      in den ersten drei Jahren etwa 12 500 Euro. Nach fünf Jahren
                      sind noch rund 9 000–10 000 Euro übrig, je nach Zustand. Mit
                      guter Pflege und geringer Laufleistung kann der Restwert auch
                      höher ausfallen. Wer das Auto schonend behandelt, es garagiert
                      und Serviceintervalle einhält, liegt im oberen Bereich der
                      Preisspanne.
                    </p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufig gestellte Fragen"
              fragen={fragenWertverlustAutoGuide}
              image={FAQImage}
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Wertverlust Auto"
                kaputtesAuto
                unfallAuto
                autoExport
                autoBewerten
                autoVerkaufen
                listenpreisBerechnen
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={höchstenPreis}
                h2
                fontSize="36px"
                title="Fazit zum Thema Wertverlust Auto"
                alt="Frau sitzt in einem Auto und nutzt ihr Smartphone, um mit Autoeinfachlos den Wertverlust ihres Autos zu berücksichtigen und zu verkaufen."
                text={
                  <>
                    <p>
                      Der Wertverlust eines Autos ist unvermeidlich, lässt sich
                      jedoch beeinflussen. Wer sein Auto pflegt, regelmäßig warten
                      lässt und den richtigen Zeitpunkt für den Verkauf wählt, kann
                      den Verlust minimieren. Eine realistische Preisvorstellung und
                      Transparenz gegenüber Käufern zahlen sich aus. Autoeinfachlos
                      unterstützt dich dabei, den exakten Marktwert zu ermitteln und
                      dein Fahrzeug zum bestmöglichen Preis zu verkaufen.
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-verkaufen"
            title="Jetzt Auto kostenlos bewerten & sicher verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default WertverlustAutoGuide;

